import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import styled from 'styled-components';
import { logError } from '../../shared/utils/logger.js';

const LoadingContainer = styled(Box)`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
`;

/**
 * RouteMapWrapper - Wrapper component to ensure map only renders on client side
 * This fixes the react-leaflet React 18 compatibility issue
 * 
 * @param {Object} props
 * @param {string} props.pickup - Pickup address (backward compatibility)
 * @param {string} props.delivery - Delivery address (backward compatibility)
 * @param {string} props.startLocation - Start address (alternative to pickup)
 * @param {string} props.endLocation - End address (alternative to delivery)
 * @param {Array} props.markers - Array of marker objects
 * @param {Array} props.route - Array of route coordinates (single route)
 * @param {Array} props.routes - Array of route objects (multiple routes)
 * @param {boolean} props.drawerExpanded - Whether drawer is expanded
 * @param {boolean} props.autoFitBounds - Whether to auto-fit bounds
 * @param {Function} props.onMarkerClick - Callback when marker is clicked
 */
const RouteMapWrapper = ({ 
  pickup,
  delivery,
  startLocation, 
  endLocation, 
  pickupAddress,
  deliveryAddress,
  markers = [],
  route = null,
  routes = null,
  drawerExpanded = false,
  autoFitBounds = true,
  onMarkerClick,
}) => {
  // Backward compatibility: use pickup/delivery if startLocation/endLocation not provided
  const start = startLocation || pickup;
  const end = endLocation || delivery;
  const [MapComponent, setMapComponent] = useState(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    
    // Dynamically import the map component only on client side
    if (typeof window !== 'undefined') {
      import('./RouteMapLeaflet.jsx').then((module) => {
        setMapComponent(() => module.default);
      }).catch((error) => {
        logError('Failed to load map component', {
          error: error.message,
        });
      });
    }
  }, []);

  if (!isMounted || !MapComponent) {
    return (
      <LoadingContainer>
        <Typography variant="body1">Loading map...</Typography>
      </LoadingContainer>
    );
  }

  return (
    <MapComponent 
      startLocation={start}
      endLocation={end}
      pickupAddress={pickupAddress}
      deliveryAddress={deliveryAddress}
      markers={markers}
      route={route}
      routes={routes}
      drawerExpanded={drawerExpanded}
      autoFitBounds={autoFitBounds}
      onMarkerClick={onMarkerClick}
    />
  );
};

export default RouteMapWrapper;

